import { UnloadingVideo } from '@sparemin/blockhead';
import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import _ from 'underscore';
import ClipSelectPlayerContainer, {
  ClipSelectPlayerProps,
} from './ClipSelectPlayer';
import { ClipSelectPlayerInstance } from './ClipSelectPlayerContents';
import useBufferingIndicator from './useBufferingIndicator';

export type ClipSelectVideoPlayerProps = Omit<
  ClipSelectPlayerProps,
  'duration' | 'children'
> & {
  /**
   * Known aspect ratio used to frame the player before the video metadata
   * loads, keeping the poster and the video consistently sized. The value
   * loaded from the video metadata takes over once available.
   */
  initialAspectRatio?: number;
  posterUrl?: string;
  videoUrl: string;
};

const Video = React.forwardRef<
  ClipSelectPlayerInstance,
  Omit<React.HTMLProps<HTMLVideoElement>, 'ref'>
>((props, ref) => {
  const videoRef = useRef<HTMLVideoElement>();

  useImperativeHandle(
    ref,
    () => ({
      pause: () => videoRef.current?.pause(),
      play: () => videoRef.current?.play(),
      getCurrentTime: () => videoRef.current?.currentTime,
      setCurrentTime: (sec: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = sec;
        }
      },
    }),
    [],
  );

  return <UnloadingVideo ref={videoRef} {...props} />;
});

const ClipSelectVideoPlayer = React.forwardRef<
  ClipSelectPlayerInstance,
  ClipSelectVideoPlayerProps
>(
  (
    { initialAspectRatio, posterUrl, videoUrl, ...props },
    forwardedVideoRef,
  ) => {
    const [duration, setDuration] = useState(0);
    const [aspectRatio, setAspectRatio] = useState<number | undefined>(
      initialAspectRatio,
    );
    // only metadata is preloaded, so playback can stall while buffering;
    // `waiting` stalls for data and `canplay`/`playing` resume once buffered
    const {
      hideBuffering,
      isBuffering,
      showBuffering,
    } = useBufferingIndicator();

    const handleLoadedMetadata = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        setDuration(video.duration);
        setAspectRatio(video.videoWidth / video.videoHeight);
      },
      [],
    );

    const fixedVideoDimension =
      !_.isFinite(aspectRatio) || aspectRatio > 1 ? 'width' : 'height';

    return (
      <ClipSelectPlayerContainer
        duration={duration}
        isLoading={isBuffering}
        ref={forwardedVideoRef}
        {...props}
      >
        {({
          className,
          onPause,
          onPlay,
          onTimeUpdate,
          ref: containerPlayerRef,
        }) => {
          const handlePause = () => {
            onPause();
            hideBuffering();
          };

          return (
            <Video
              aria-label="Clip preview video"
              className={className}
              controls={false}
              onCanPlay={hideBuffering}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={onPlay}
              onPlaying={hideBuffering}
              onPause={handlePause}
              onWaiting={showBuffering}
              onTimeUpdate={onTimeUpdate}
              poster={posterUrl}
              preload="metadata"
              ref={containerPlayerRef}
              src={videoUrl}
              style={{
                [fixedVideoDimension]: '100%',
              }}
              tabIndex={-1}
            />
          );
        }}
      </ClipSelectPlayerContainer>
    );
  },
);

export default ClipSelectVideoPlayer;
